// @tennis-matches/core/router/legacy-adapter
// Legacy Router API adapter that maps to React Router
// This allows gradual migration while maintaining backward compatibility

import {
  TRAININGS_DESK_HUB_PATH,
  TRAININGS_DESK_PATH_PREFIX,
  TRAININGS_DESK_URL_SEGMENT_ALIASES,
  TRAININGS_DESK_HUB_SUB_SEGMENTS,
  TRAININGS_DESK_PUBLIC_INVITE_ROUTE,
  isTrainingsDeskInviteSubSegment,
} from "../constants/trainingsDesk.js";
import {
  REGELTERMINE_HUB_PATH,
  REGELTERMINE_URL_SEGMENT_ALIASES,
  REGELTERMINE_HUB_SUB_SEGMENTS,
  buildRegelterminePath,
} from "../constants/regeltermine.js";
import {
  CONTACTS_ADMIN_LIST_PATH,
  canonicalizeContactsAdminPathname,
  isContactsAdminPathSegment,
} from "../constants/contactsAdmin.js";

/**
 * Merge two query strings safely.
 * Values from `override` win over `base`.
 * @param {string} base
 * @param {string} override
 * @returns {string} merged query including leading "?" or ""
 */
function mergeQuery(base, override) {
  const a = new URLSearchParams(String(base || "").replace(/^\?/, ""));
  const b = new URLSearchParams(String(override || "").replace(/^\?/, ""));
  for (const [k, v] of b.entries()) a.set(k, v);
  const s = a.toString();
  return s ? `?${s}` : "";
}

/**
 * @returns {string} hash without leading "#"
 */
function getHashRaw() {
  const h = String(window.location.hash || "").trim();
  return h.startsWith("#") ? h.slice(1) : h;
}

/**
 * Helper to map route names to React Router paths
 * This should match the routeToPath function in App.jsx
 */
function routeToPath(routeName) {
  const routeMap = {
    home: "/",
    "legacy-home": "/",
    hub: "/",
    "hub-matchmixer": "/hub/matchmixer",
    "hub-matchmixer-anmeldungen": "/hub/matchmixer/anmeldungen",
    "hub-matchmixer-open": "/hub/matchmixer/open",
    "hub-matchmixer-templates": "/hub/matchmixer/vorlagen",
    "hub-matchmixer-turniere": "/hub/matchmixer/turniere",
    "hub-matchmixer-statistik": "/hub/matchmixer/statistik",
    /** @deprecated Legacy Hub-URL */
    "hub-schleifchen": "/hub/matchmixer",
    "matchmixer-anmelden": "/matchmixer/anmelden",
    "matchmixer-me": "/matchmixer/me",
    "hub-trainerstunden": TRAININGS_DESK_HUB_PATH,
    "hub-trainingsdesk": TRAININGS_DESK_HUB_PATH,
    "hub-trainer": "/hub/trainer",
    "hub-clubmeisterschaft": "/hub/clubmeisterschaft",
    "hub-clubmeisterschaft-statistik": "/hub/clubmeisterschaft/statistik",
    "hub-insights": "/hub/insights",
    "hub-matchflow": "/hub/matchflow",
    matchflow: "/matchflow",
    "matchflow-accept": "/hub/matchflow/accept",
    insights: "/hub/insights",
    statistik: "/hub/insights",
    "hub-medenspiele": "/hub/medenspiele",
    "hub-belegung": "/hub/belegung",
    belegung: "/hub/belegung",
    "hub-kalender": "/hub/kalender",
    kalender: "/hub/kalender",
    "hub-winterdoppel": REGELTERMINE_HUB_PATH,
    "hub-regeltermine": REGELTERMINE_HUB_PATH,
    "hub-serientermine": REGELTERMINE_HUB_PATH,
    winterdoppel: REGELTERMINE_HUB_PATH,
    regeltermine: REGELTERMINE_HUB_PATH,
    serientermine: REGELTERMINE_HUB_PATH,
    "winterdoppel-series": buildRegelterminePath("series"),
    "winterdoppel-players": buildRegelterminePath("players"),
    "winterdoppel-lineup": buildRegelterminePath("saisonplanung/gruppen"),
    "winterdoppel-plan": buildRegelterminePath("plan"),
    "winterdoppel-week": buildRegelterminePath("week"),
    "winterdoppel-fairness": buildRegelterminePath("fairness"),
    "winterdoppel-costs": buildRegelterminePath("kosten"),
    "winterdoppel-teilnehmer": buildRegelterminePath("teilnehmer"),
    "winterdoppel-einstellungen": buildRegelterminePath("einstellungen"),
    "winterdoppel-saisonplanung": buildRegelterminePath("saisonplanung"),
    "winterdoppel-termine": buildRegelterminePath("termine"),
    "winterdoppel-auswertung": buildRegelterminePath("auswertung"),
    "regeltermine-teilnehmer": buildRegelterminePath("teilnehmer"),
    "regeltermine-einstellungen": buildRegelterminePath("einstellungen"),
    "regeltermine-saisonplanung": buildRegelterminePath("saisonplanung"),
    "regeltermine-termine": buildRegelterminePath("termine"),
    "regeltermine-auswertung": buildRegelterminePath("auswertung"),
    "winterdoppel-join": buildRegelterminePath("join"),
    "winterdoppel-me": buildRegelterminePath("me"),
    "winterdoppel-confirm-email": buildRegelterminePath("confirm-email"),
    "winterdoppel-schedule": buildRegelterminePath("schedule"),
    "regeltermine-series": buildRegelterminePath("series"),
    "regeltermine-players": buildRegelterminePath("players"),
    "regeltermine-lineup": buildRegelterminePath("saisonplanung/gruppen"),
    "regeltermine-plan": buildRegelterminePath("plan"),
    "regeltermine-week": buildRegelterminePath("week"),
    "regeltermine-fairness": buildRegelterminePath("fairness"),
    "regeltermine-costs": buildRegelterminePath("kosten"),
    "regeltermine-teilnehmer": buildRegelterminePath("teilnehmer"),
    "regeltermine-einstellungen": buildRegelterminePath("einstellungen"),
    "regeltermine-saisonplanung": buildRegelterminePath("saisonplanung"),
    "regeltermine-termine": buildRegelterminePath("termine"),
    "regeltermine-auswertung": buildRegelterminePath("auswertung"),
    "regeltermine-join": buildRegelterminePath("join"),
    "regeltermine-me": buildRegelterminePath("me"),
    "regeltermine-confirm-email": buildRegelterminePath("confirm-email"),
    "regeltermine-schedule": buildRegelterminePath("schedule"),
    "hub-clubhub": "/hub/clubhub",
    "hub-tennis-live": "/tenniswelt",
    "hub-tour": "/tenniswelt",
    tenniswelt: "/tenniswelt",
    "hub-ki-assistant": "/hub/ki-assistent",
    matchmixer: "/matchmixer",
    "matchmixer-setup": "/matchmixer/setup",
    "matchmixer-setup-players": "/matchmixer/setup/players",
    "matchmixer-schedule": "/matchmixer/schedule",
    "matchmixer-entry": "/matchmixer/entry",
    "matchmixer-results": "/matchmixer/results",
    "matchmixer-round-config": "/matchmixer/round-config",
    "matchmixer-finish": "/matchmixer/finish",
    /** Legacy Routen-Keys → kanonische MatchMixer-URLs */
    schleifchen: "/matchmixer",
    "schleifchen-setup": "/matchmixer/setup",
    "schleifchen-schedule": "/matchmixer/schedule",
    "schleifchen-entry": "/matchmixer/entry",
    "schleifchen-results": "/matchmixer/results",
    "schleifchen-round-config": "/matchmixer/round-config",
    "schleifchen-finish": "/matchmixer/finish",
    clubmeisterschaft: "/clubmeisterschaft",
    "clubmeisterschaft-setup": "/clubmeisterschaft/setup",
    "clubmeisterschaft-schedule": "/clubmeisterschaft/schedule",
    "clubmeisterschaft-entry": "/clubmeisterschaft/entry",
    "clubmeisterschaft-finish": "/clubmeisterschaft/finish",
    medenspiele: "/medenspiele",
    "medenspiele-kader": "/medenspiele/kader",
    "medenspiele-termine": "/medenspiele/termine",
    "medenspiele-training": "/medenspiele/training",
    "medenspiele-planning": "/medenspiele/training",
    minigames: "/minigames",
    trainerstunden: TRAININGS_DESK_PATH_PREFIX,
    trainingsdesk: TRAININGS_DESK_PATH_PREFIX,
    trainer: "/trainer",
    clubhub: "/clubhub",
    // Alias-Routen (Produkt-URLs) – dieselbe App wie die kanonischen Pfade
    coachdesk: TRAININGS_DESK_PATH_PREFIX,
    courtleague: "/courtleague",
    courtlink: "/courtlink",
    teamdesk: "/teamdesk",
    clubchamp: "/clubchamp",
    "player-matches": "/player-matches",
    "accept-invite": "/accept-invite",
    "access-pending": "/access-pending",
    "auth-callback": "/auth/callback",
    "reset-password": "/reset-password",
    "public-parent-confirm": "/public/parent-confirm",
    "public-session-rsvp": "/public/session-rsvp",
    "public-regeltermine-swap": "/public/regeltermine/swap",
    "public-pay": "/public/pay",
    "public-pay-success": "/public/pay/success",
    "public-pay-cancel": "/public/pay/cancel",
    "public-booking": "/public/booking",
    "admin-people": CONTACTS_ADMIN_LIST_PATH,
    "admin-locations": "/verwaltung/orte",
    profile: "/profile",
    settings: "/hub/einstellungen",
    "hub-settings": "/hub/einstellungen",
    einstellungen: "/hub/einstellungen",
    "hub-einstellungen": "/hub/einstellungen",
    "dev-qa": "/dev/qa",
  };
  // Query und Hash erhalten (Deep-Links z. B. …/einstellungen#ttb-settings-paypal)
  const raw = String(routeName || "");
  const hashIdx = raw.indexOf("#");
  const hash = hashIdx >= 0 ? raw.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw;
  const [route, query] = withoutHash.split("?");
  const isAbsolutePath = typeof route === "string" && route.startsWith("/");
  const path = isAbsolutePath ? route : routeMap[route] || `/${route}`;
  return `${path}${query ? `?${query}` : ""}${hash}`;
}

/**
 * Gets the current route from the URL.
 * Migrates legacy hash routes to clean URLs.
 * @returns {string} route name (defaults to "hub" for "/")
 */
export function getRoute() {
  const pathname = String(window.location.pathname || "");

  // API-Routen sind kein SPA-Screen (PayPal-Redirect etc.) → Express direkt ansprechen.
  if (pathname.startsWith("/api/")) {
    try {
      const port = String(window.location.port || "");
      const host = String(window.location.hostname || "");
      const apiOrigin =
        host === "localhost" || host === "127.0.0.1"
          ? port === "5174"
            ? window.location.origin
            : "http://localhost:5174"
          : window.location.origin;
      const target = `${apiOrigin}${pathname}${window.location.search || ""}${window.location.hash || ""}`;
      if (target !== window.location.href) {
        window.location.replace(target);
      }
    } catch (_) {
      // ignore
    }
    return "hub";
  }

  // Public pages (no login required)
  if (pathname.startsWith("/public/parent-confirm/")) {
    return "public-parent-confirm";
  }
  if (pathname.startsWith("/public/session-rsvp/")) {
    return "public-session-rsvp";
  }
  if (pathname.startsWith("/public/regeltermine/swap/")) {
    return "public-regeltermine-swap";
  }
  if (
    pathname === "/public/pay/success" ||
    pathname.startsWith("/public/pay/success/")
  ) {
    return "public-pay-success";
  }
  if (
    pathname === "/public/pay/cancel" ||
    pathname.startsWith("/public/pay/cancel/")
  ) {
    return "public-pay-cancel";
  }
  if (pathname.startsWith("/public/pay/")) {
    return "public-pay";
  }
  if (
    pathname === "/public/booking" ||
    pathname.startsWith("/public/booking/")
  ) {
    return "public-booking";
  }

  // Explicit auth callback route
  if (pathname === "/auth/callback" || pathname.startsWith("/auth/callback/")) {
    return "auth-callback";
  }

  // Dev QA desk (localhost only, rendered in web shell)
  if (pathname === "/dev/qa" || pathname.startsWith("/dev/qa/")) {
    return "dev-qa";
  }

  // Normal clean URL routing
  const cleanPath = pathname.replace(/^\/+/, "");
  const segments = cleanPath.split("/").filter(Boolean);

  /** Produkt-URL-Aliase → internes Route-Segment (Route-Namen & Loader unverändert). */
  const FIRST_SEGMENT_ALIASES = Object.freeze({
    ...TRAININGS_DESK_URL_SEGMENT_ALIASES,
    ...REGELTERMINE_URL_SEGMENT_ALIASES,
    courtleague: "clubmeisterschaft",
    courtlink: "player-matches",
    teamdesk: "medenspiele",
    clubchamp: "clubmeisterschaft",
    "tennis-live": "tenniswelt",
    einstellungen: "settings",
  });
  if (segments.length && FIRST_SEGMENT_ALIASES[segments[0]]) {
    segments[0] = FIRST_SEGMENT_ALIASES[segments[0]];
  }
  if (pathname === "/tennis-live" || pathname.startsWith("/tennis-live/")) {
    window.history.replaceState(
      null,
      "",
      `${pathname.replace(/^\/tennis-live/, "/tenniswelt")}${window.location.search || ""}`,
    );
  }

  // "/" or "" -> Hub is the new default home
  if (segments.length === 0) return "hub";

  // "/legacy-home" or "/home" -> Hub (no legacy home route)
  if (segments[0] === "legacy-home" || segments[0] === "home") return "hub";

  let route = segments[0];

  if (
    route === "trainerstunden" &&
    segments[1] &&
    isTrainingsDeskInviteSubSegment(segments[1])
  ) {
    return TRAININGS_DESK_PUBLIC_INVITE_ROUTE;
  }

  if (route === "zugriff-ausstehend") return "access-pending";

  // Verwaltung: /verwaltung/kontakte, /verwaltung/kontakte/:id, /verwaltung/orte
  if (route === "verwaltung") {
    const sub = String(segments[1] || "");
    if (isContactsAdminPathSegment(sub)) {
      const canonicalPath = canonicalizeContactsAdminPathname(pathname);
      if (canonicalPath) {
        const nextUrl = `${canonicalPath}${window.location.search || ""}${window.location.hash || ""}`;
        window.history.replaceState(null, "", nextUrl);
      }
      if (String(segments[2] || "").trim()) return "admin-people-detail";
      return "admin-people";
    }
    if (sub === "orte" || sub === "locations") return "admin-locations";
    return "verwaltung";
  }

  // Hub: /hub -> "hub", dedicated hub pages keep "hub-*".
  // Other hub subroutes (e.g. /hub/trainerstunden) should resolve directly
  // to the app route ("trainerstunden"), otherwise ReactApp treats them as
  // hub pages and renders no content.
  if (route === "hub") {
    const sub = segments[1] || "";
    if (!sub) return "hub";
    // Marketplace App-Detail v0: /hub/apps/:appKey → ReactApp rendert hub-app-detail (appKey aus pathname).
    if (sub === "apps" && segments[2]) {
      return "hub-app-detail";
    }
    if (sub === "ki-assistent" || sub === "ki") return "hub-ki-assistant";
    if (sub === "einstellungen" || sub === "settings") return "settings";
    if (sub === "matchmixer" || sub === "schleifchen") {
      if (segments[2] === "anmeldungen") return "hub-matchmixer-anmeldungen";
      if (segments[2] === "turniere") return "hub-matchmixer-turniere";
      if (segments[2] === "statistik" || segments[2] === "stats")
        return "hub-matchmixer-statistik";
      if (segments[2] === "vorlagen" || segments[2] === "templates")
        return "hub-matchmixer-templates";
      return "hub-matchmixer";
    }
    if (TRAININGS_DESK_HUB_SUB_SEGMENTS.includes(sub)) return "trainerstunden";
    if (REGELTERMINE_HUB_SUB_SEGMENTS.includes(sub)) return "winterdoppel";
    if (sub === "insights" || sub === "statistik") {
      return "insights";
    }
    if (sub === "matchflow") {
      if (segments[2] === "accept" && segments[3]) {
        return "matchflow-accept";
      }
      return "matchflow";
    }
    if (
      sub === "clubmeisterschaft" ||
      sub === "tennis-live" ||
      sub === "tour" ||
      sub === "tenniswelt"
    ) {
      if (sub === "tennis-live" || sub === "tour" || sub === "tenniswelt") {
        const rest = segments.slice(2).filter(Boolean).join("/");
        const path = rest ? `/tenniswelt/${rest}` : "/tenniswelt";
        window.history.replaceState(
          null,
          "",
          `${path}${window.location.search || ""}`,
        );
        return "tenniswelt";
      }
      if (
        sub === "clubmeisterschaft" &&
        (segments[2] === "statistik" || segments[2] === "stats")
      ) {
        return "hub-clubmeisterschaft-statistik";
      }
      return `hub-${sub}`;
    }
    return sub;
  }

  // Clubmeisterschaft: /clubmeisterschaft -> "clubmeisterschaft", /clubmeisterschaft/setup -> "clubmeisterschaft-setup", etc.
  const CLUB_PATH_TO_ROUTE = {
    setup: "clubmeisterschaft-setup",
    schedule: "clubmeisterschaft-schedule",
    entry: "clubmeisterschaft-entry",
    finish: "clubmeisterschaft-finish",
  };
  if (route === "clubmeisterschaft") {
    const sub = segments[1] || "";
    return sub
      ? CLUB_PATH_TO_ROUTE[sub] || `clubmeisterschaft-${sub}`
      : "clubmeisterschaft";
  }

  const MATCHMIXER_SUB_TO_ROUTE = {
    setup: "matchmixer-setup",
    schedule: "matchmixer-schedule",
    finish: "matchmixer-finish",
    entry: "matchmixer-entry",
    results: "matchmixer-results",
    "round-config": "matchmixer-round-config",
    anmelden: "matchmixer-anmelden",
    me: "matchmixer-me",
  };

  // Kanonisch: /matchmixer[/step[/sub]]]
  if (route === "matchmixer") {
    const sub = segments[1] || "";
    const third = segments[2] || "";
    if (sub === "setup" && third === "players")
      return "matchmixer-setup-players";
    return sub
      ? MATCHMIXER_SUB_TO_ROUTE[sub] || `matchmixer-${sub}`
      : "matchmixer";
  }

  // Legacy: /schleifchen[/step] (und App-Host hat früher nur /schleifchen als Root genutzt)
  if (route === "schleifchen") {
    const sub = segments[1] || "";
    const third = segments[2] || "";
    if (sub === "setup" && third === "players")
      return "matchmixer-setup-players";
    return sub
      ? MATCHMIXER_SUB_TO_ROUTE[sub] || `matchmixer-${sub}`
      : "matchmixer";
  }

  // Legacy: Standalone-Segment-Name „schleifchenturnier“
  if (route === "schleifchenturnier") {
    const sub = segments[1] || "";
    const third = segments[2] || "";
    if (sub === "setup" && third === "players")
      return "matchmixer-setup-players";
    return sub
      ? MATCHMIXER_SUB_TO_ROUTE[sub] || `matchmixer-${sub}`
      : "matchmixer";
  }

  // Legacy: Top-Level-Kurzpfade /setup, /schedule, … → MatchMixer
  if (MATCHMIXER_SUB_TO_ROUTE[route]) {
    return MATCHMIXER_SUB_TO_ROUTE[route];
  }

  const TEAMDESK_SUB_TO_ROUTE = {
    kader: "medenspiele-kader",
    squad: "medenspiele-kader",
    termine: "medenspiele-termine",
    sessions: "medenspiele-termine",
    training: "medenspiele-training",
    planung: "medenspiele-training",
    planning: "medenspiele-training",
  };
  if (route === "medenspiele") {
    const sub = segments[1] || "";
    return sub
      ? TEAMDESK_SUB_TO_ROUTE[sub] || `medenspiele-${sub}`
      : "medenspiele";
  }

  const TRAINER_SUB_TO_ROUTE = {
    sessions: "trainer-sessions",
    "lesson-plans": "trainer-lesson-plans",
    drills: "trainer-drills",
    students: "trainer-students",
  };
  if (route === "trainer") {
    const sub = segments[1] || "";
    const third = segments[2] || "";
    if (sub === "sessions" && third === "new") return "trainer-session-new";
    if (sub === "sessions" && third) return "trainer-session-detail";
    if (sub === "lesson-plans" && third === "new")
      return "trainer-lesson-plan-new";
    if (sub === "lesson-plans" && third) return "trainer-lesson-plan-detail";
    if (sub === "students" && third === "new") return "trainer-student-new";
    if (sub === "students" && third) return "trainer-student-detail";
    if (sub === "drills" && third) return "trainer-drill-detail";
    return sub ? TRAINER_SUB_TO_ROUTE[sub] || `trainer-${sub}` : "trainer";
  }

  if (route === "winterdoppel") {
    const sub = segments[1] || "";
    if (sub === "join") return "winterdoppel-join";
    if (sub === "me") return "winterdoppel-me";
    if (sub === "confirm-email") return "winterdoppel-confirm-email";
    if (sub === "schedule") return "winterdoppel-schedule";
    if (sub === "series" || sub === "einstellungen")
      return "winterdoppel-einstellungen";
    if (sub === "players" || sub === "teilnehmer")
      return "winterdoppel-teilnehmer";
    if (sub === "lineup" || sub === "planungsregeln")
      return "winterdoppel-saisonplanung";
    if (sub === "plan" || sub === "saisonplanung") {
      return "winterdoppel-saisonplanung";
    }
    if (sub === "week" || sub === "termine") return "winterdoppel-termine";
    if (sub === "fairness" || sub === "auswertung")
      return "winterdoppel-auswertung";
    if (sub === "costs") return "winterdoppel-costs";
    return sub ? `winterdoppel-${sub}` : "winterdoppel";
  }

  if (route === "superadmin-clubs") {
    const sub = segments[1] || "";
    if (sub === "import") return "superadmin-clubs-import";
    if (sub === "add") return "superadmin-clubs-add";
    if (sub === "discover") return "superadmin-clubs-discover";
    if (sub === "duplicates") return "superadmin-clubs-duplicates";
    return "superadmin-clubs";
  }

  if (route === "tennis-live") {
    window.history.replaceState(
      null,
      "",
      `${pathname.replace(/^\/tennis-live/, "/tenniswelt")}${window.location.search || ""}`,
    );
    return "tenniswelt";
  }

  if (route === "matchflow") {
    if (segments[1] === "accept" && segments[2]) return "matchflow-accept";
    return "matchflow";
  }

  if (route !== "home") return route;

  // Legacy hash support + migration (only for hash-based routes)
  const raw = getHashRaw().replace(/^\/+/, "");
  if (!raw) return "hub";

  // legacy: #share=<token>
  if (raw.startsWith("share=")) {
    return "share";
  }

  // hash paths: #share/<token>, #player-matches?x=1
  const [hashPath, hashQuery] = raw.split("?");
  const hashRoute = hashPath.split("/")[0] || "hub";

  if (hashRoute && hashRoute !== "home") {
    const mergedQuery = mergeQuery(window.location.search, hashQuery || "");
    const newPath = `/${hashRoute}${mergedQuery}`;
    window.history.replaceState(null, "", newPath);
    return hashRoute;
  }

  return "hub";
}

/**
 * Extracts the share token from URL.
 * Supports:
 *   /share/<token>
 *   #share/<token>
 *   #share=<token>
 * @returns {string}
 */
export function getShareToken() {
  const pathname = String(window.location.pathname || "");

  // /share/<token>
  if (pathname.startsWith("/share/")) {
    return pathname.slice(7).split("/")[0].split("?")[0];
  }

  // legacy hash
  const raw = getHashRaw().replace(/^\/+/, "");

  if (raw.startsWith("share/")) {
    return raw.slice(6).split("/")[0].split("?")[0];
  }

  if (raw.startsWith("share=")) {
    return raw.slice(6).split("?")[0];
  }

  return "";
}

/**
 * Navigate to a route using React Router if available, otherwise History API.
 * @param {string} route - route name or path (e.g. "home", "schleifchen", "/x?y=1")
 * @param {boolean} replace
 */
/**
 * Absolute URL (pathname + optional search + hash) für React Router.
 * @param {string} absolute
 * @returns {{ pathname: string, search: string, hash: string }}
 */
function absoluteUrlToLocation(absolute) {
  const raw = String(absolute || "").trim() || "/";
  const hashIdx = raw.indexOf("#");
  const hash = hashIdx >= 0 ? raw.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw;
  const qIdx = withoutHash.indexOf("?");
  const pathname =
    (qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash) || "/";
  const search = qIdx >= 0 ? withoutHash.slice(qIdx) : "";
  return { pathname, search, hash };
}

export function navigateTo(route, replace = false) {
  let r = String(route || "").trim();

  // Use React Router if available (for apps using React Router)
  if (typeof window !== "undefined" && window.__reactRouterNavigate) {
    const navigate = window.__reactRouterNavigate;
    const path = !r || r === "#" ? "/" : routeToPath(r);
    navigate(absoluteUrlToLocation(path), { replace });
    return;
  }

  // Fallback to History API (legacy)
  if (!r || r === "#" || r === "home") {
    const path = "/";
    replace
      ? window.history.replaceState(null, "", path)
      : window.history.pushState(null, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }

  // Absolute Pfade inkl. Query/Hash über routeToPath normalisieren
  if (r.startsWith("/")) {
    const path = routeToPath(r);
    replace
      ? window.history.replaceState(null, "", path)
      : window.history.pushState(null, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }

  // remove legacy markers
  r = r.replace(/^#/, "").replace(/^\/+/, "");

  const hashIdx = r.indexOf("#");
  const hash = hashIdx >= 0 ? r.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? r.slice(0, hashIdx) : r;
  const [pathPart, queryPart] = withoutHash.split("?");
  const path = `/${pathPart}${queryPart ? `?${queryPart}` : ""}${hash}`;

  replace
    ? window.history.replaceState(null, "", path)
    : window.history.pushState(null, "", path);

  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Register callback for route changes.
 * @param {Function} cb
 */
export function onRouteChange(cb) {
  window.addEventListener("popstate", cb);

  // legacy hashchange → migrate once, then notify
  window.addEventListener("hashchange", () => {
    const route = getRoute();
    if (route !== "home") {
      cb();
    }
  });
}
